import { useState, useRef } from 'react';
import { ReactSketchCanvasRef } from 'react-sketch-canvas';
import { 
  exportCanvasDrawing, 
  combineImageWithDrawing, 
  convertDataUrlToBlob 
} from '../utils/imageSaveLogic';
import { ImageDimensions } from '../utils/imageEditConstants';

export const useCanvasOperations = (
  onSave?: (editedImageBlob: Blob) => void,
  onCancel?: () => void
) => {
  const canvasRef = useRef<ReactSketchCanvasRef>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Track undo/redo history manually
  const [undoStack, setUndoStack] = useState<any[]>([]);
  const [redoStack, setRedoStack] = useState<any[]>([]);

  const updateUndoRedoState = async () => {
    if (canvasRef.current) {
      try {
        const paths = await canvasRef.current.exportPaths();
        setCanUndo(paths.length > 0);
        // canRedo wird durch die manuellen Stacks verwaltet
      } catch (err) {
        console.error('Failed to update undo/redo state:', err);
      }
    }
  };

  const handleUndo = async () => {
    if (canvasRef.current && canUndo) {
      try {
        // Aktuellen Zustand in Redo-Stack speichern
        const currentPaths = await canvasRef.current.exportPaths();
        setRedoStack(prev => [currentPaths, ...prev]);
        setCanRedo(true);
        
        // Undo ausführen
        await canvasRef.current.undo();
        updateUndoRedoState();
      } catch (err) {
        console.error('Undo failed:', err);
      }
    }
  };

  const handleRedo = async () => {
    if (canvasRef.current && canRedo && redoStack.length > 0) {
      try {
        // Letzten Zustand aus Redo-Stack wiederherstellen
        const pathsToRestore = redoStack[0];
        const newRedoStack = redoStack.slice(1);
        
        setRedoStack(newRedoStack);
        setCanRedo(newRedoStack.length > 0);
        
        // Pfade wiederherstellen
        await canvasRef.current.loadPaths(pathsToRestore);
        updateUndoRedoState();
      } catch (err) {
        console.error('Redo failed:', err);
      }
    }
  };

  const handleClear = async () => {
    if (canvasRef.current) {
      try {
        // Aktuellen Zustand in Redo-Stack speichern vor dem Löschen
        const currentPaths = await canvasRef.current.exportPaths();
        if (currentPaths.length > 0) {
          setRedoStack([currentPaths]);
          setCanRedo(true);
        }
        
        await canvasRef.current.clearCanvas();
        updateUndoRedoState();
      } catch (err) {
        console.error('Clear failed:', err);
      }
    }
  };

  const handleStrokeEnd = () => {
    // Neuer Strich wurde gemacht - Redo-Stack leeren
    setRedoStack([]);
    setCanRedo(false);
    updateUndoRedoState();
  };

  const handleSave = async (
    backgroundImage: string | null,
    imageDimensions: ImageDimensions,
    displayDimensions: ImageDimensions,
    strokeColor: string,
    strokeWidth: number,
    setError: (error: string | null) => void
  ) => {
    if (!canvasRef.current) {
      console.log('Canvas not ready or not available');
      return;
    }

    console.log('Starting save process (unified logic for all devices)...');
    
    setIsLoading(true);
    setError(null);

    try {
      if (backgroundImage) {
        console.log('Saving with background image...');
        console.log('Image dimensions:', imageDimensions);
        console.log('Display dimensions:', displayDimensions);
        
        const drawingImageData = await exportCanvasDrawing(
          canvasRef.current,
          displayDimensions,
          strokeColor,
          strokeWidth
        );
        
        const blob = await combineImageWithDrawing(
          backgroundImage,
          drawingImageData,
          imageDimensions,
          displayDimensions
        );

        if (onSave) {
          console.log('Saving edited image blob:', { size: blob.size, type: blob.type });
          onSave(blob);
          if (onCancel) {
            setTimeout(() => {
              console.log('Closing modal after save');
              onCancel();
            }, 500);
          }
        } else {
          console.error('onSave callback not provided');
          setError('Speicher-Callback nicht verfügbar');
        }
      } else {
        console.log('Saving without background image...');
        
        const imageData = await exportCanvasDrawing(
          canvasRef.current,
          displayDimensions,
          strokeColor,
          strokeWidth
        );
        
        console.log('Drawing data exported successfully');
        
        const blob = await convertDataUrlToBlob(imageData);

        if (onSave) {
          console.log('Saving drawing blob, size:', blob.size);
          onSave(blob);
          if (onCancel) {
            setTimeout(() => {
              console.log('Closing modal after save');
              onCancel();
            }, 500);
          }
        } else {
          console.error('onSave callback not provided');
          setError('Speicher-Callback nicht verfügbar');
        }
      }
    } catch (err) {
      console.error('Save failed with error:', err);
      
      let errorMessage = 'Unbekannter Fehler';
      if (err instanceof Error) {
        errorMessage = err.message || 'Error ohne Nachricht';
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err && typeof err === 'object') {
        errorMessage = JSON.stringify(err);
      } else {
        errorMessage = String(err);
      }
      
      setError('Fehler beim Speichern des bearbeiteten Bildes: ' + errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (
    backgroundImage: string | null,
    imageDimensions: ImageDimensions,
    setError: (error: string | null) => void
  ) => {
    try {
      let imageData;

      if (backgroundImage) {
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');

        if (!tempCtx) {
          throw new Error('Canvas Context konnte nicht erstellt werden');
        }

        tempCanvas.width = imageDimensions.width;
        tempCanvas.height = imageDimensions.height;

        const backgroundImg = new Image();
        
        // Einheitliches CrossOrigin-Handling für Download
        const isBlobUrl = backgroundImage.startsWith('blob:');
        
        if (!isBlobUrl) {
          backgroundImg.crossOrigin = 'anonymous';
        }

        await new Promise((resolve, reject) => {
          backgroundImg.onload = resolve;
          backgroundImg.onerror = (error) => {
            console.error('Failed to load background image for download:', error);
            const retryImg = new Image();
            retryImg.onload = () => {
              resolve(null);
            };
            retryImg.onerror = reject;
            retryImg.src = backgroundImage;
          };
          backgroundImg.src = backgroundImage;
        });

        tempCtx.drawImage(backgroundImg, 0, 0, imageDimensions.width, imageDimensions.height);

        const drawingImageData = await canvasRef.current?.exportImage('png');
        if (!drawingImageData) {
          throw new Error('Could not export drawing data');
        }

        const drawingImg = new Image();
        await new Promise((resolve, reject) => {
          drawingImg.onload = resolve;
          drawingImg.onerror = reject;
          drawingImg.src = drawingImageData;
        });

        tempCtx.drawImage(drawingImg, 0, 0);

        imageData = tempCanvas.toDataURL('image/png');
      } else {
        imageData = await canvasRef.current?.exportImage('png');
        if (!imageData) {
          throw new Error('Could not export image data');
        }
      }

      const link = document.createElement('a');
      link.download = `bearbeitetes-bild-${Date.now()}.png`;
      link.href = imageData;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Fehler beim Herunterladen des Bildes');
      console.error('Download failed:', err);
    }
  };

  return {
    canvasRef,
    canUndo,
    canRedo,
    isLoading,
    handleUndo,
    handleRedo,
    handleClear,
    handleStrokeEnd,
    handleSave,
    handleDownload
  };
};
